import type React from 'react';
import { useMemo, useRef, useState } from 'react';
import type { Control, FieldValues, Path, RegisterOptions } from 'react-hook-form';

import { AutocompleteContext, AutocompleteDisplay, AutocompleteList } from './_autocompleteSlots';
import type { DisplaySlotProps, DropdownOption, ListSlotProps, WithMinSelections } from './types';
import VNDropdownBase from './VNDropdownBase';
import VNDropdownRhf from './VNDropdownRhf';

// ─── Shared props ─────────────────────────────────────────────────────────────

interface SharedProps {
  options: DropdownOption[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  /** Replace the default trigger display with a custom render slot */
  displayComponent?: React.ComponentType<DisplaySlotProps>;
  /** Replace the default option list with a custom render slot */
  listComponent?: React.ComponentType<ListSlotProps>;
  /** Remove the border from the trigger button */
  disableBorder?: boolean;
  /** Compact mode: fixed 121px trigger with no border, count badge instead of chips */
  compact?: boolean;
  /** Override the popover paper width in pixels */
  popoverWidth?: number;
  /** Override the popover paper minimum height in pixels */
  popoverMinHeight?: number;
  /** Additional CSS class(es) applied to the root element */
  className?: string;
  /**
   * Search/filter variant to use.
   * - `"autocomplete"` — the trigger becomes an inline editable field when open.
   *   `listComponent` is used as the inner list renderer inside the filter layer.
   *   `displayComponent` is ignored (managed internally).
   * - `"inset"` — a search input is shown inside the popover above the list.
   *   The trigger display is unchanged; filtering is handled internally.
   */
  searchable?: 'autocomplete' | 'inset';
}

// ─── RHF mode — name + control ────────────────────────────────────────────────

interface VNDropdownRhfMode<TFieldValues extends FieldValues = FieldValues> extends SharedProps {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  // Exclude standalone props
  value?: never;
  onChange?: never;
  onBlur?: never;
  error?: never;
}

// ─── Standalone controlled mode — value + onChange ────────────────────────────

interface VNDropdownStandaloneMode extends SharedProps {
  // Exclude RHF props
  name?: never;
  control?: never;
  rules?: never;
  /** Current value — required in standalone mode */
  value: DropdownOption['id'] | Array<DropdownOption['id']> | null;
  /** Called whenever the selection changes */
  onChange: (value: DropdownOption['id'] | Array<DropdownOption['id']> | null) => void;
  onBlur?: () => void;
  /** External validation error message */
  error?: string;
}

export type VNDropdownProps<TFieldValues extends FieldValues = FieldValues> =
  | VNDropdownRhfMode<TFieldValues>
  | VNDropdownStandaloneMode;

const VNDropdown = <TFieldValues extends FieldValues = FieldValues>(props: VNDropdownProps<TFieldValues>) => {
  const [query, setQuery] = useState('');
  const selectFocusedRef = useRef<(() => void) | null>(null);

  const {
    options,
    label,
    placeholder,
    disabled,
    multiple,
    displayComponent,
    listComponent,
    disableBorder,
    searchable,
    compact,
    popoverWidth,
    popoverMinHeight,
    className,
  } = props;

  const resolvedDisplay = searchable === 'autocomplete' ? AutocompleteDisplay : displayComponent;
  const resolvedList = searchable === 'autocomplete' ? AutocompleteList : listComponent;

  // Preserve the minSelections marker from the original listComponent even when
  // the autocomplete variant replaces it with AutocompleteList as the resolved slot.
  const minSelections = (listComponent as React.ComponentType<ListSlotProps> & WithMinSelections)?.minSelections;

  const contextValue = useMemo(() => ({ query, setQuery, selectFocusedRef, listComponent }), [query, listComponent]);

  const sharedSlotProps = useMemo(
    () => ({
      options,
      label,
      placeholder,
      disabled,
      multiple,
      displayComponent: resolvedDisplay,
      listComponent: resolvedList,
      disableBorder,
      compact,
      popoverWidth,
      popoverMinHeight,
      className,
      insetSearch: searchable === 'inset',
      minSelections,
    }),
    [
      options,
      label,
      placeholder,
      disabled,
      multiple,
      resolvedDisplay,
      resolvedList,
      disableBorder,
      compact,
      popoverWidth,
      popoverMinHeight,
      className,
      searchable,
      minSelections,
    ]
  );

  const inner =
    props.control !== undefined ? (
      <VNDropdownRhf
        {...sharedSlotProps}
        name={(props as VNDropdownRhfMode<TFieldValues>).name}
        control={(props as VNDropdownRhfMode<TFieldValues>).control}
        rules={(props as VNDropdownRhfMode<TFieldValues>).rules}
      />
    ) : (
      <VNDropdownBase
        {...sharedSlotProps}
        value={(props as VNDropdownStandaloneMode).value}
        onChange={(props as VNDropdownStandaloneMode).onChange}
        onBlur={(props as VNDropdownStandaloneMode).onBlur}
        error={(props as VNDropdownStandaloneMode).error}
      />
    );

  return searchable === 'autocomplete' ? (
    <AutocompleteContext.Provider value={contextValue}>{inner}</AutocompleteContext.Provider>
  ) : (
    inner
  );
};

export default VNDropdown;
